import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ProjectResource } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { useGiveBonusMutation } from "../../services/projectJob.service";
import { toast } from "react-toastify";
import SelectSimple from "../../components/SelectSimple";
import BaseModal from "../../components/global/BaseModal";
import StyledTextField from "../../components/global/StyledTextField";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalBonusFormProps {
  open: boolean;
  onClose: () => void;
  job: ProjectResource | null;
}

const ModalBonusForm = ({ open, onClose, job }: ModalBonusFormProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const type = watch("type");

  const [giveBonus, { isLoading }] = useGiveBonusMutation();

  // UI Labels
  const sendOtherExpensesLabel = useUILabel("project.sendOtherExpenses");
  const bonusLabel = useUILabel("project.bonus");
  const additionalHoursLabel = useUILabel("project.additionalHours");
  const reimbursementLabel = useUILabel("project.reimbursement");
  const selectTypeLabel = useUILabel("project.selectType");
  const amountLabel = useUILabel("project.amount");
  const reasonLabel = useUILabel("project.reason");
  const otherExpensesSentSuccessfullyLabel = useUILabel("project.otherExpensesSentSuccessfully");
  const pleaseEnterValidAmountLabel = useUILabel("project.pleaseEnterValidAmount");
  const pleaseProvideReasonLabel = useUILabel("project.pleaseProvideReason");
  const recordOtherExpensesDescriptionLabel = useUILabel("project.recordOtherExpensesDescription");
  const cancelLabel = useUILabel("common.cancel");
  const submitLabel = useUILabel("common.submit");

  const spendingOptions = React.useMemo(() => [
    { value: "bonus", label: bonusLabel },
    { value: "additional hours", label: additionalHoursLabel },
    { value: "reimbursement", label: reimbursementLabel },
  ], [bonusLabel, additionalHoursLabel, reimbursementLabel]);

  const onSubmit = async (payload: any) => {
    try {
      if (!job) return;

      await giveBonus({
        project_id: job?.project_id,
        resource_id: job?.resource_id,
        total: payload.total,
        type: payload.type,
        user_comment: payload.user_comment || "",
      }).unwrap();
      toast.success(otherExpensesSentSuccessfullyLabel);
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || "Failed to send other expenses");
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("total", 0);
      setValue("type", "bonus");
      setValue("user_comment", "");
      clearErrors();
    }
  }, [job, open]);

  const footer = (
    <>
      <Button onClick={onClose}>{cancelLabel}</Button>
      <LoadingButton type="submit" form="bonus-form" variant="contained" loading={isLoading}>
        {submitLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={sendOtherExpensesLabel}
      footer={footer}
      maxWidth="xs"
    >
      <Box
        component="form"
        id="bonus-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          p: 3,
          display: "grid",
          gap: 2,
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {recordOtherExpensesDescriptionLabel}
        </Typography>
        <Controller
          name="type"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value, ref } }) => (
            <SelectSimple
              title={selectTypeLabel}
              data={spendingOptions}
              value={value}
              sx={{ mt: 0.5 }}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name="total"
          control={control}
          rules={{ required: true, min: 1 }}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <StyledTextField
              label={amountLabel}
              value={value}
              fullWidth
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.total}
              helperText={!!errors.total && pleaseEnterValidAmountLabel}
              type="number"
              inputProps={{ min: 0 }}
            />
          )}
        />
        {type == "reimbursement" && (
          <Controller
            name="user_comment"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <StyledTextField
                label={reasonLabel}
                value={value}
                fullWidth
                multiline
                minRows={3}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.user_comment}
                helperText={!!errors.user_comment && pleaseProvideReasonLabel}
              />
            )}
          />
        )}
      </Box>
    </BaseModal>
  );
};
export default ModalBonusForm;
